import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { createPenalty, disciplineStage } from "../../api/penalties";
import { DisciplineLadder } from "./DisciplineLadder";
import { OverrideDialog } from "./OverrideDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { PenaltyAudience } from "./PenaltyAudience";

const ORDER = ["warning", "first_warning", "final_warning", "termination", "dismissal"];

// نافذة إصدار عقوبة (في منتصف الصفحة): الفرع ← القسم ← الموظف ← نوع العقوبة ← السبب.
export const PenaltyForm = ({ employees, branches, departments, reasons, canOverride, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ user_id: "", penalty_type: "warning", reason_category: "violation", reason_details: "" });
  const [override, setOverride] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (overrideReason) => {
    try {
      await createPenalty({ ...form, override_progressive: Boolean(overrideReason), override_reason: overrideReason || "" });
      toast.success(t("penalty.saved"));
      setOverride(null);
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.code === "penalty_target_forbidden" ? t("penalty.ownerForbidden")
        : typeof detail === "string" ? detail : t("penalty.saveFailed");
      toast.error(msg);
    }
  };

  const check = async () => {
    if (!form.user_id) return toast.error(t("penalty.pickEmployee"));
    if (form.reason_category === "other" && !form.reason_details.trim()) return toast.error(t("penalty.detailsRequired"));
    const stage = await disciplineStage(form.user_id);
    const nextIndex = Math.min(stage.last_severity_index + 1, ORDER.length - 1);
    const selectedIndex = ORDER.indexOf(form.penalty_type);
    if (selectedIndex !== nextIndex) {
      const deserved = t(`penalty.types.${ORDER[nextIndex]}`);
      if (!canOverride) return toast.error(t("penalty.ladderBlocked", { deserved }));
      return setOverride({ current: deserved, level: t(`penalty.types.${form.penalty_type}`) });
    }
    return setConfirm(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/80 p-4"
         data-testid="penalty-form-modal" onClick={onClose}>
      <NeuCard className="grid max-h-[90vh] w-full max-w-2xl gap-3 overflow-y-auto" data-testid="penalty-form" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="grid h-10 w-10 place-items-center rounded-2xl text-destructive shadow-neu-in"><AlertTriangle className="h-5 w-5" /></span>
            <div className="grid">
              <h3 className="text-base font-black leading-tight">{t("penalty.issue")}</h3>
              <p className="text-xs text-muted-foreground">{t("penalty.issueHint")}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="penalty-form-x" className="rounded-xl p-1.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <PenaltyAudience branches={branches} departments={departments} employees={employees}
                           value={form.user_id} onPick={(id) => setForm((f) => ({ ...f, user_id: id }))} />
          <DisciplineLadder userId={form.user_id} />
          <label className="grid gap-1 text-xs font-bold">
            {t("penalty.fields.type")}
            <NeuSelect data-testid="penalty-type-select" value={form.penalty_type} onChange={set("penalty_type")}>
              {ORDER.map((key) => <option key={key} value={key}>{t(`penalty.types.${key}`)}</option>)}
            </NeuSelect>
          </label>
          <label className="grid gap-1 text-xs font-bold">
            {t("penalty.fields.reason")}
            <NeuSelect data-testid="penalty-reason-select" value={form.reason_category} onChange={set("reason_category")}>
              {Object.keys(reasons).map((key) => <option key={key} value={key}>{t(`penalty.reasons.${key}`)}</option>)}
            </NeuSelect>
          </label>
          <label className="grid gap-1 text-xs font-bold sm:col-span-2">
            {t("penalty.fields.details")}
            <NeuInput data-testid="penalty-details-input" value={form.reason_details} onChange={set("reason_details")}
                      placeholder={t("penalty.fields.detailsHint")} />
          </label>
        </div>
        <div className="flex flex-wrap gap-3">
          <NeuButton className="bg-destructive text-destructive-foreground hover:brightness-110"
                     data-testid="penalty-submit-btn" onClick={check}>{t("penalty.submit")}</NeuButton>
          <NeuButton data-testid="penalty-form-close" onClick={onClose}>{t("common.cancel")}</NeuButton>
        </div>
        <OverrideDialog open={Boolean(override)} current={override?.current} level={override?.level}
                        onCancel={() => setOverride(null)} onConfirm={submit} />
        <ConfirmDialog open={confirm} title={t("penalty.confirm.title")} body={t("penalty.confirm.body")}
                       onCancel={() => setConfirm(false)} onConfirm={() => { setConfirm(false); submit(null); }} />
      </NeuCard>
    </div>
  );
};
